import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'

type VerifyState = 'loading' | 'success' | 'error'

export function AuthVerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<VerifyState>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [countdown, setCountdown] = useState(2)

  const token = searchParams.get('token')
  const redirectTo = searchParams.get('redirect') || '/'

  useEffect(() => {
    if (!token) {
      setState('error')
      setErrorMessage('No verification token found. Please request a new magic link.')
      return
    }

    let cancelled = false

    async function verify() {
      try {
        const res = await fetch(`/api/auth/verify?token=${encodeURIComponent(token!)}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        })

        if (cancelled) return

        const data = await res.json()

        if (!res.ok || !data.success) {
          setState('error')
          setErrorMessage(data.error || 'Verification failed. Please request a new magic link.')
          return
        }

        setState('success')
      } catch {
        if (!cancelled) {
          setState('error')
          setErrorMessage('Network error. Please check your connection and try again.')
        }
      }
    }

    verify()
    return () => { cancelled = true }
  }, [token])

  useEffect(() => {
    if (state !== 'success') return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          navigate(redirectTo, { replace: true })
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [state, navigate, redirectTo])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-xl)',
        background: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-2xl) var(--space-xl)',
          boxShadow: 'var(--shadow-md)',
          textAlign: 'center',
        }}
      >
        {state === 'loading' && (
          <>
            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--color-text)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Verifying your link...
            </p>
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
              Please wait a moment.
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--color-correct)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              You're signed in!
            </p>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
              Redirecting in {countdown}...
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--color-incorrect)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Verification failed
            </p>
            <p
              style={{
                fontSize: 14,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-lg)',
                lineHeight: 1.5,
              }}
            >
              {errorMessage}
            </p>
            <Link
              to="/login"
              style={{
                fontSize: 14,
                color: 'var(--color-accent)',
                textDecoration: 'none',
              }}
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
